export type ConfirmStepRef = {id: string; dependsOn?: string};

export type ConfirmResultRow = {
    id: string;
    ok: boolean;
    dryRunId?: string;
    planHash?: string;
    message?: string;
};

export function isStepApplyOk(
    applyResults: ConfirmResultRow[],
    stepId: string,
): boolean {
    return applyResults.some((r) => r.id === stepId && r.ok);
}

export function pendingConfirmSteps<T extends ConfirmStepRef>(
    steps: T[],
    applyResults: ConfirmResultRow[],
): T[] {
    return steps.filter((s) => !isStepApplyOk(applyResults, s.id));
}

function isConfirmDryRunReady(
    confirmResults: ConfirmResultRow[],
    stepId: string,
): boolean {
    const byId = new Map(confirmResults.map((r) => [r.id, r]));
    const r = byId.get(stepId);
    return Boolean(r && r.ok && r.dryRunId && r.planHash);
}

function isDependentReadyInBatch(
    step: ConfirmStepRef,
    applyResults: ConfirmResultRow[],
    pendingIds: Set<string>,
): boolean {
    const dep = step.dependsOn;
    if (!dep) {
        return true;
    }
    // Parent already applied, or parent is in the same Apply batch (plan order runs it first).
    return isStepApplyOk(applyResults, dep) || pendingIds.has(dep);
}

export function canApplyConfirmSteps(
    steps: ConfirmStepRef[],
    confirmResults: ConfirmResultRow[],
    applyResults: ConfirmResultRow[],
): boolean {
    const pending = pendingConfirmSteps(steps, applyResults);
    if (!pending.length) {
        return false;
    }

    const independent = pending.filter((s) => !s.dependsOn);
    const dependent = pending.filter((s) => s.dependsOn);
    const pendingIds = new Set(pending.map((s) => s.id));

    if (independent.length) {
        const independentsReady = independent.every((s) =>
            isConfirmDryRunReady(confirmResults, s.id),
        );
        if (!independentsReady) {
            return false;
        }
    }

    if (dependent.length) {
        return dependent.every((s) =>
            isDependentReadyInBatch(s, applyResults, pendingIds),
        );
    }

    return true;
}

export function canApplyPending(
    pendingSteps: ConfirmStepRef[],
    confirmResults: ConfirmResultRow[],
    applyResults: ConfirmResultRow[],
): boolean {
    return canApplyConfirmSteps(pendingSteps, confirmResults, applyResults);
}

export function mergeConfirmResults(
    existing: ConfirmResultRow[],
    updates: ConfirmResultRow[],
): ConfirmResultRow[] {
    const map = new Map(existing.map((r) => [r.id, r]));
    for (const u of updates) {
        map.set(u.id, u);
    }
    return [...map.values()];
}
